import { useEffect, useId, useRef, useState } from 'react';
import { fieldDir } from '../i18n/field-dir';
import { caughtErrorMessage, serverErrorMessage } from '../i18n/server-errors';
import { t } from '../services/language-manager';
import {
  CATALOG_ROW_UI_GATE,
  CATALOG_SEARCH_LIMIT,
  deleteActionCatalogProduct,
  searchActionCatalog,
  upsertActionCatalogProduct,
  type CatalogProductRow,
  type CatalogSummary,
} from '../services/wizard-backend';

interface CatalogRowManagerProps {
  genesysOrgId: string;
  genesysRegion: string;
  actionId: string;
  catalogSummary: CatalogSummary | null;
  onCatalogChanged: (summary: CatalogSummary) => void;
}

interface ProductForm {
  sku: string;
  productName: string;
  description: string;
  unit: string;
  packageSize: string;
  aliases: string;
  active: boolean;
}

const EMPTY_FORM: ProductForm = {
  sku: '',
  productName: '',
  description: '',
  unit: '',
  packageSize: '',
  aliases: '',
  active: true,
};

// Mirrors the toolbar's CATALOG_FIELD_MAX, which would otherwise silently
// truncate a long alias and drop aliases past the 50th.
const ALIAS_MAX_LENGTH = 256;
const ALIASES_MAX_COUNT = 50;

/** Returns the i18n key of the first violated alias limit, or null. */
function validateAliases(raw: string): string | null {
  const seen = new Set<string>();
  for (const part of raw.split('|')) {
    const alias = part.replace(/\s+/g, ' ').trim();
    if (!alias) continue;
    if (alias.length > ALIAS_MAX_LENGTH) return 'settings-actions-catalog-row-alias-too-long';
    seen.add(alias.toLowerCase());
    if (seen.size > ALIASES_MAX_COUNT) return 'settings-actions-catalog-row-aliases-too-many';
  }
  return null;
}

function rowToForm(row: CatalogProductRow): ProductForm {
  return {
    sku: row.sku,
    productName: row.productName,
    description: row.description,
    unit: row.unit || '',
    packageSize: row.packageSize != null ? String(row.packageSize) : '',
    aliases: row.aliases.join('|'),
    active: row.active,
  };
}

/**
 * Surgical Add / Search / Edit / Delete for catalogs below the 500-product
 * gate. CSV upload stays the bulk replace path. Search is server-side.
 */
export function CatalogRowManager({
  genesysOrgId,
  genesysRegion,
  actionId,
  catalogSummary,
  onCatalogChanged,
}: CatalogRowManagerProps) {
  const id = useId();
  const productCount = catalogSummary?.products ?? 0;
  const hasProducts = productCount > 0;
  const showTable = hasProducts && productCount < CATALOG_ROW_UI_GATE;
  const aboveGate = productCount >= CATALOG_ROW_UI_GATE;

  const [form, setForm] = useState<ProductForm>(EMPTY_FORM);
  // Collapsed by default; an empty catalog opens it because Add is the only CTA there.
  const [formOpen, setFormOpen] = useState(!hasProducts);
  const [editingSku, setEditingSku] = useState<string | null>(null);
  const formRef = useRef<HTMLDivElement | null>(null);
  const scrollToFormOnOpen = useRef(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [formNotice, setFormNotice] = useState<string | null>(null);
  const [pendingOverwrite, setPendingOverwrite] = useState<{ sku: string; productName: string } | null>(null);

  const [query, setQuery] = useState('');
  const [results, setResults] = useState<CatalogProductRow[]>([]);
  const [total, setTotal] = useState(0);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<CatalogProductRow | null>(null);
  const [deleting, setDeleting] = useState(false);
  const searchTimer = useRef<number | null>(null);
  // Monotonic request id so a slow earlier search cannot overwrite a newer one.
  const searchSeq = useRef(0);

  useEffect(() => () => {
    if (searchTimer.current) window.clearTimeout(searchTimer.current);
  }, []);

  useEffect(() => {
    if (!hasProducts) setFormOpen(true);
  }, [hasProducts]);

  // Edit opens the form from a row further down the page; bring it back into view.
  useEffect(() => {
    if (formOpen && scrollToFormOnOpen.current) {
      scrollToFormOnOpen.current = false;
      formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [formOpen, editingSku]);

  const patchForm = (patch: Partial<ProductForm>) => {
    setForm((prev) => ({ ...prev, ...patch }));
    setFormError(null);
    setFormNotice(null);
    setPendingOverwrite(null);
  };

  const clearForm = () => {
    setForm(EMPTY_FORM);
    setEditingSku(null);
    setPendingOverwrite(null);
    setFormError(null);
  };

  // Cancel / save collapse the form, except on an empty catalog where Add is the only CTA.
  const resetForm = () => {
    clearForm();
    setFormOpen(!hasProducts);
  };

  const toggleForm = () => {
    if (formOpen) {
      clearForm();
      setFormOpen(false);
      return;
    }
    setFormNotice(null);
    setFormOpen(true);
  };

  const saveProduct = async (overwrite: boolean) => {
    const sku = form.sku.trim();
    const productName = form.productName.trim();
    if (!sku || !productName) {
      setFormError(t('settings-actions-catalog-row-required'));
      return;
    }
    let packageSize: number | null = null;
    if (form.packageSize.trim()) {
      const n = Number(form.packageSize.trim());
      if (!Number.isFinite(n) || n <= 0) {
        setFormError(t('settings-actions-catalog-row-package-size'));
        return;
      }
      packageSize = n;
    }
    const aliasError = validateAliases(form.aliases);
    if (aliasError) {
      setFormError(t(aliasError));
      return;
    }

    setSaving(true);
    setFormError(null);
    setFormNotice(null);
    try {
      const result = await upsertActionCatalogProduct(genesysOrgId, genesysRegion, actionId, {
        sku,
        productName,
        description: form.description,
        unit: form.unit,
        packageSize,
        aliases: form.aliases,
        active: form.active,
        overwrite,
        // Edit mode only: a row deleted by someone else meanwhile must not be re-created.
        ...(editingSku !== null ? { expectExisting: true } : {}),
      });
      if (!result.ok) {
        if (result.error.code === 'SKU_CONFLICT' && result.error.status === 409) {
          setPendingOverwrite({
            sku: result.error.sku || sku,
            productName: result.error.productName || '',
          });
          return;
        }
        if (result.error.code === 'SKU_NOT_FOUND') {
          // Keep the values, drop back to Add mode so the SKU unlocks and the next save creates.
          setFormError(t('settings-actions-catalog-row-removed-meanwhile'));
          setEditingSku(null);
          setResults((prev) => prev.filter((r) => r.sku.toUpperCase() !== sku.toUpperCase()));
          setTotal((prev) => Math.max(0, prev - 1));
          return;
        }
        if (result.error.code === 'CATALOG_CONFLICT') {
          setFormError(t('settings-actions-catalog-row-catalog-changed'));
          return;
        }
        setFormError(serverErrorMessage({
          code: result.error.code,
          cause: result.error.message,
          knownKey: 'settings-actions-catalog-row-save-error',
          keepDetail: true,
        }));
        return;
      }
      onCatalogChanged(result.catalogSummary);
      setFormNotice(result.created
        ? t('settings-actions-catalog-row-added')
        : t('settings-actions-catalog-row-updated'));
      setPendingOverwrite(null);
      setForm(EMPTY_FORM);
      setEditingSku(null);
      setFormOpen(false);
      if (query.trim()) void runSearch(query);
    } catch (e) {
      setFormError(caughtErrorMessage(e, 'settings-actions-catalog-row-save-error'));
    } finally {
      setSaving(false);
    }
  };

  const runSearch = async (q: string) => {
    const term = q.trim();
    const seq = ++searchSeq.current;
    if (!term) {
      setResults([]);
      setTotal(0);
      setSearchError(null);
      setSearching(false);
      return;
    }
    setSearching(true);
    setSearchError(null);
    try {
      const result = await searchActionCatalog(genesysOrgId, genesysRegion, actionId, term, {
        limit: CATALOG_SEARCH_LIMIT,
      });
      if (seq !== searchSeq.current) return;
      if (!result.ok) {
        setResults([]);
        setTotal(0);
        setSearchError(serverErrorMessage({
          code: result.error.code,
          cause: result.error.message,
          knownKey: 'settings-actions-catalog-row-search-error',
          keepDetail: true,
        }));
        return;
      }
      setResults(result.results);
      setTotal(result.total);
    } catch (e) {
      if (seq !== searchSeq.current) return;
      setResults([]);
      setTotal(0);
      setSearchError(caughtErrorMessage(e, 'settings-actions-catalog-row-search-error'));
    } finally {
      if (seq === searchSeq.current) setSearching(false);
    }
  };

  const onQueryChange = (value: string) => {
    setQuery(value);
    if (searchTimer.current) window.clearTimeout(searchTimer.current);
    searchTimer.current = window.setTimeout(() => {
      void runSearch(value);
    }, 250);
  };

  const startEdit = (row: CatalogProductRow) => {
    setForm(rowToForm(row));
    setEditingSku(row.sku);
    setPendingOverwrite(null);
    setFormError(null);
    setFormNotice(null);
    setPendingDelete(null);
    scrollToFormOnOpen.current = true;
    setFormOpen(true);
  };

  const confirmDelete = async () => {
    if (!pendingDelete || deleting) return;
    setDeleting(true);
    setSearchError(null);
    try {
      const result = await deleteActionCatalogProduct(
        genesysOrgId,
        genesysRegion,
        actionId,
        pendingDelete.sku,
      );
      if (!result.ok) {
        setSearchError(result.error.code === 'LAST_PRODUCT' || result.error.code === 'CATALOG_CONFLICT'
          ? serverErrorMessage({ code: result.error.code })
          : serverErrorMessage({
            code: result.error.code,
            cause: result.error.message,
            knownKey: 'settings-actions-catalog-row-delete-error',
            keepDetail: true,
          }));
        return;
      }
      onCatalogChanged(result.catalogSummary);
      setResults((prev) => prev.filter((r) => r.sku.toUpperCase() !== pendingDelete.sku.toUpperCase()));
      setTotal((prev) => Math.max(0, prev - 1));
      if (editingSku && editingSku.toUpperCase() === pendingDelete.sku.toUpperCase()) {
        resetForm();
      }
      setPendingDelete(null);
    } catch (e) {
      setSearchError(caughtErrorMessage(e, 'settings-actions-catalog-row-delete-error'));
    } finally {
      setDeleting(false);
    }
  };

  const formId = `${id}-form`;
  const formTitle = editingSku
    ? t('settings-actions-catalog-row-title-edit')
    : hasProducts
      ? t('settings-actions-catalog-row-title')
      : t('settings-actions-catalog-row-title-empty');

  return (
    <div className="catalog-rows">
      <button
        type="button"
        className="catalog-rows__toggle"
        aria-expanded={formOpen}
        aria-controls={formId}
        onClick={toggleForm}
      >
        <svg
          className={`catalog-rows__chevron${formOpen ? ' catalog-rows__chevron--open' : ''}`}
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <polyline points="9 18 15 12 9 6" />
        </svg>
        {formTitle}
      </button>
      {aboveGate && (
        <p className="form-hint">{t('settings-actions-catalog-row-large')}</p>
      )}

      <div className="catalog-rows__form" id={formId} ref={formRef} hidden={!formOpen}>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label" htmlFor={`${id}-sku`}>
              {t('settings-actions-catalog-row-sku')}
            </label>
            <input
              id={`${id}-sku`}
              dir={fieldDir(form.sku, editingSku ? '' : t('settings-actions-catalog-row-sku-placeholder'), true)}
              className="form-input"
              type="text"
              maxLength={128}
              value={form.sku}
              readOnly={Boolean(editingSku)}
              placeholder={editingSku ? undefined : t('settings-actions-catalog-row-sku-placeholder')}
              aria-describedby={`${id}-sku-help`}
              onChange={(e) => patchForm({ sku: e.target.value })}
            />
            <p className="form-hint" id={`${id}-sku-help`}>
              {editingSku
                ? t('settings-actions-catalog-row-sku-locked')
                : t('settings-actions-catalog-row-sku-help')}
            </p>
          </div>
          <div className="form-group">
            <label className="form-label" htmlFor={`${id}-name`}>
              {t('settings-actions-catalog-row-name')}
            </label>
            <input
              id={`${id}-name`}
              dir={fieldDir(form.productName)}
              className="form-input"
              type="text"
              maxLength={256}
              value={form.productName}
              onChange={(e) => patchForm({ productName: e.target.value })}
            />
          </div>
        </div>
        <div className="form-group">
          <label className="form-label" htmlFor={`${id}-desc`}>
            {t('settings-actions-catalog-row-description')}
          </label>
          <input
            id={`${id}-desc`}
            dir={fieldDir(form.description)}
            className="form-input"
            type="text"
            maxLength={2000}
            value={form.description}
            onChange={(e) => patchForm({ description: e.target.value })}
          />
        </div>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label" htmlFor={`${id}-unit`}>
              {t('settings-actions-catalog-row-unit')}
            </label>
            <input
              id={`${id}-unit`}
              dir={fieldDir(form.unit)}
              className="form-input"
              type="text"
              maxLength={64}
              value={form.unit}
              onChange={(e) => patchForm({ unit: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label className="form-label" htmlFor={`${id}-pack`}>
              {t('settings-actions-catalog-row-package-size-label')}
            </label>
            <input
              id={`${id}-pack`}
              className="form-input"
              type="text"
              inputMode="decimal"
              value={form.packageSize}
              onChange={(e) => patchForm({ packageSize: e.target.value })}
            />
          </div>
        </div>
        <div className="form-group">
          <label className="form-label" htmlFor={`${id}-aliases`}>
            {t('settings-actions-catalog-row-aliases')}
          </label>
          <input
            id={`${id}-aliases`}
            dir={fieldDir(form.aliases, t('settings-actions-catalog-row-aliases-placeholder'))}
            className="form-input"
            type="text"
            value={form.aliases}
            placeholder={t('settings-actions-catalog-row-aliases-placeholder')}
            onChange={(e) => patchForm({ aliases: e.target.value })}
          />
        </div>
        <label className="catalog-rows__active">
          <input
            type="checkbox"
            checked={form.active}
            onChange={(e) => patchForm({ active: e.target.checked })}
          />
          {t('settings-actions-catalog-row-active')}
        </label>
        <div className="action-card__actions">
          <button
            type="button"
            className="btn btn--primary btn--small"
            disabled={saving}
            onClick={() => void saveProduct(editingSku !== null || pendingOverwrite !== null)}
          >
            {saving
              ? t('settings-actions-catalog-row-saving')
              : editingSku
                ? t('settings-actions-catalog-row-save-edit')
                : t('settings-actions-catalog-row-add')}
          </button>
          <button
            type="button"
            className="btn btn--secondary btn--small"
            disabled={saving}
            onClick={resetForm}
          >
            {t('settings-actions-catalog-row-cancel')}
          </button>
        </div>
        {pendingOverwrite && (
          <div className="settings-page__notice settings-page__notice--warning action-catalog__confirm" role="status">
            <p>
              {t('settings-actions-catalog-row-overwrite', {
                sku: pendingOverwrite.sku,
                name: pendingOverwrite.productName || t('settings-actions-catalog-row-unnamed'),
              })}
            </p>
            <div className="action-catalog__confirm-actions">
              <button
                type="button"
                className="btn btn--primary btn--small"
                disabled={saving}
                onClick={() => void saveProduct(true)}
              >
                {t('settings-actions-catalog-row-overwrite-continue')}
              </button>
              <button
                type="button"
                className="btn btn--secondary btn--small"
                disabled={saving}
                onClick={() => setPendingOverwrite(null)}
              >
                {t('settings-actions-catalog-replace-cancel')}
              </button>
            </div>
          </div>
        )}
        {formError && (
          <p className="form-error" role="alert">{formError}</p>
        )}
      </div>
      {/* Outside the collapsible body so it stays visible after a save collapses the form. */}
      {formNotice && (
        <p className="form-hint catalog-rows__notice" role="status">{formNotice}</p>
      )}

      {showTable && (
        <div className="catalog-rows__search">
          <label className="form-label" htmlFor={`${id}-search`}>
            {t('settings-actions-catalog-row-search')}
          </label>
          <input
            id={`${id}-search`}
            dir={fieldDir(query, t('settings-actions-catalog-row-search-placeholder'))}
            className="form-input"
            type="search"
            value={query}
            placeholder={t('settings-actions-catalog-row-search-placeholder')}
            onChange={(e) => onQueryChange(e.target.value)}
          />
          {searching && <p className="form-hint">{t('settings-actions-catalog-row-searching')}</p>}
          {searchError && <p className="form-error" role="alert">{searchError}</p>}
          {pendingDelete && (
            <div className="settings-page__notice settings-page__notice--warning action-catalog__confirm" role="status">
              <p>
                {t('settings-actions-catalog-row-delete-confirm', {
                  sku: pendingDelete.sku,
                  name: pendingDelete.productName,
                })}
              </p>
              <div className="action-catalog__confirm-actions">
                <button
                  type="button"
                  className="btn btn--primary btn--small"
                  disabled={deleting}
                  onClick={() => void confirmDelete()}
                >
                  {deleting
                    ? t('settings-actions-catalog-row-deleting')
                    : t('settings-actions-catalog-row-delete')}
                </button>
                <button
                  type="button"
                  className="btn btn--secondary btn--small"
                  disabled={deleting}
                  onClick={() => setPendingDelete(null)}
                >
                  {t('settings-actions-catalog-replace-cancel')}
                </button>
              </div>
            </div>
          )}
          {query.trim() && !searching && results.length === 0 && !searchError && (
            <p className="form-hint">{t('settings-actions-catalog-row-no-results')}</p>
          )}
          {query.trim() && !searching && results.length > 0 && (
            <p className="form-hint catalog-rows__status" role="status">
              {total > results.length
                ? t('settings-actions-catalog-row-matches-capped', {
                  shown: String(results.length),
                  total: String(total),
                })
                : total === 1
                  ? t('settings-actions-catalog-row-match-one')
                  : t('settings-actions-catalog-row-matches', { total: String(total) })}
            </p>
          )}
          {results.length > 0 && (
            <div className="catalog-rows__table-wrap">
              <table className="catalog-rows__table" dir="ltr">
                <thead>
                  <tr>
                    <th>{t('settings-actions-catalog-row-sku')}</th>
                    <th>{t('settings-actions-catalog-row-name')}</th>
                    <th>{t('settings-actions-catalog-row-status')}</th>
                    <th>{t('settings-actions-catalog-row-actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {results.map((row) => (
                    <tr key={row.sku}>
                      <td><code>{row.sku}</code></td>
                      <td><bdi>{row.productName}</bdi></td>
                      <td>
                        {row.active
                          ? t('settings-actions-catalog-row-status-active')
                          : t('settings-actions-catalog-row-status-inactive')}
                      </td>
                      <td>
                        <div className="catalog-rows__row-actions">
                          <button
                            type="button"
                            className="btn btn--secondary btn--small"
                            onClick={() => startEdit(row)}
                          >
                            {t('settings-actions-catalog-row-edit')}
                          </button>
                          <button
                            type="button"
                            className="btn btn--secondary btn--small"
                            onClick={() => setPendingDelete(row)}
                          >
                            {t('settings-actions-catalog-row-delete')}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
